import { useMemo, useState } from 'react';

import { FormControl, FormHelperText } from '@mui/material';
import { TimePicker, type TimePickerProps } from '@mui/x-date-pickers-pro';
import { Clock } from 'lucide-react';
import { type Control, Controller, type FieldError } from 'react-hook-form';

import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { useLocale } from '@shared/hooks/locale';

type RaTimeProps = Omit<
  TimePickerProps,
  'value' | 'onChange' | 'variant' | 'size' | 'InputProps' | 'name'
> & {
  control: Control<any>;
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
};

type RaTimeFieldProps = Omit<RaTimeProps, 'control' | 'name'> & {
  label: string;
  required?: boolean;
  value: string | null;
  onChange: (event: any) => void;
  disabled?: boolean;
  error?: FieldError;
};

const RaTimeField = ({
  label,
  required = false,
  value,
  onChange,
  disabled = false,
  error,
}: RaTimeFieldProps) => {
  const [open, setOpen] = useState(false);

  const timeValue = useMemo(() => {
    if (!value) {
      return null;
    }

    try {
      // Parse HH:mm format to Date object
      const [hours, minutes] = value.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date;
    } catch (error) {
      console.error('time parsing error:', error);
      return null;
    }
  }, [value]);

  const handleChange = (time: Date | null) => {
    try {
      if (!time) {
        onChange(null);
        return;
      }

      // Convert Date to HH:mm format
      const hours = time.getHours().toString().padStart(2, '0');
      const minutes = time.getMinutes().toString().padStart(2, '0');
      onChange(`${hours}:${minutes}`);
    } catch (error) {
      console.error('time change error:', error);
    }
  };

  return (
    <FormControl
      fullWidth
      error={!!error}
      // Same box as RaDate: the two sit side by side in activity dialogs.
      sx={{
        '& .MuiPickersInputBase-root': {
          height: 37,
          borderRadius: '8px',
          fontSize: '0.875rem',
          background: 'var(--mui-palette-background-paper)',
          '.dark &': {
            background: 'rgba(255, 255, 255, 0.05)',
          },
        },
        '& .MuiPickersOutlinedInput-notchedOutline': {
          borderRadius: '8px',
        },
      }}
    >
      <RaLabel label={label} required={required} />
      <TimePicker
        value={timeValue}
        onChange={handleChange}
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        // MUI X v9 defaults to false on the time picker, which adds a
        // Cancel/OK bar wider than the clock; the date picker defaults to true.
        closeOnSelect
        slots={{
          openPickerIcon: () => <Clock size={16} />,
        }}
        slotProps={{
          textField: {
            variant: 'outlined',
            size: 'small',
          },
        }}
      />
      {error?.message && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  );
};

export const RaTime = ({
  control,
  name,
  label,
  required = false,
  disabled = false,
}: RaTimeProps) => {
  const { t } = useLocale();

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? t('This field is required') : undefined,
      }}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <RaTimeField
          label={label}
          required={required}
          value={value as string | null}
          onChange={onChange}
          disabled={disabled}
          error={error}
        />
      )}
    />
  );
};

export default RaTime;
